import React, { useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { useAuth } from '../../context/AuthContext';
import { api, apiEndpoints } from '../../utils/apiService';

interface PosConfigTabProps {
  storeOptions: SearchableSelectOption[];
  activeSubtab?: 'poshub';
}

interface ProvisionResult {
  ok?: boolean;
  store_id?: number;
  store_group_id?: number;
  reseller_id?: string;
  account_created?: boolean;
  location_created?: boolean;
  store_group_integration?: {
    id?: number;
    environment?: string;
    external_account_id?: string;
  };
  connection?: {
    connection_pk?: number;
    environment?: string;
    status?: string;
    is_active?: boolean;
    is_primary_for_orders?: boolean;
    is_primary_for_menu_sync?: boolean;
    poshub_reseller_id?: string;
    poshub_account_id?: string;
    poshub_location_id?: string;
    poshub_connection_id?: string;
    poshub_connection_status?: string;
  };
}

const textAreaClassName =
  'w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800';

const selectClassName =
  'h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800';

const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'CAD', label: 'CAD' },
  { value: 'GBP', label: 'GBP' },
];

const stringifyErrors = (errors: unknown): string => {
  if (!errors) return 'Validation failed.';
  if (typeof errors === 'string') return errors;
  try {
    return JSON.stringify(errors, null, 2);
  } catch {
    return 'Validation failed.';
  }
};

const parseJsonObject = (
  value: string,
  label: string,
): Record<string, unknown> => {
  if (!value.trim()) return {};
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
};

const readProvisionError = async (
  response: Response | null,
): Promise<string> => {
  if (!response) return 'No response from server.';
  if (response.status === 403) return 'Platform admin access required.';

  const body = await response.json().catch(() => null);
  if (response.status === 502 && body?.error === 'poshub_api_error') {
    const statusCode = body.status_code ? ` (status ${body.status_code})` : '';
    const detail = body.detail ? `: ${body.detail}` : '.';
    return `Retryable POSHUB API error${statusCode}${detail}`;
  }

  if (body?.error === 'validation_failed') {
    return body.detail || 'Validation failed.';
  }

  if (body?.errors) {
    return stringifyErrors(body.errors);
  }

  if (body?.detail || body?.message || body?.error) {
    return String(body.detail || body.message || body.error);
  }

  return `Failed to provision POSHUB location (status ${response.status}).`;
};

const ResultRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-2xl border border-[rgb(var(--app-border)/0.7)] bg-[rgb(var(--app-surface-muted)/0.55)] p-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--app-muted))]">
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-semibold text-[rgb(var(--app-text))]">
      {value || '-'}
    </div>
  </div>
);

const PosConfigTab: React.FC<PosConfigTabProps> = ({
  storeOptions,
  activeSubtab = 'poshub',
}) => {
  const { user } = useAuth();
  const canProvision =
    user?.is_platform_admin === true || user?.email === 'sp@askarthur.io';
  const selectedSubtab = activeSubtab;
  const [formData, setFormData] = useState({
    store_id: '',
    reseller_id: '',
    reseller_name: '',
    country_code: '',
    currency: '',
    phone_number: '',
    account_payload_overrides: '',
    location_payload_overrides: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ProvisionResult | null>(null);

  if (!canProvision) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProvision = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const storeId = Number(formData.store_id);
    if (!storeId) {
      setError('Select a store before provisioning.');
      return;
    }
    if (!formData.country_code) {
      setError('Select a country code before provisioning.');
      return;
    }
    if (!formData.currency) {
      setError('Select a currency before provisioning.');
      return;
    }

    const confirmed = window.confirm(
      'This will create or reuse POSHUB account and location records for the selected store. Continue?',
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const accountOverrides = parseJsonObject(
        formData.account_payload_overrides,
        'Account payload overrides',
      );
      const locationOverrides = parseJsonObject(
        formData.location_payload_overrides,
        'Location payload overrides',
      );

      const payload: Record<string, unknown> = { store_id: storeId };
      if (formData.reseller_id.trim()) {
        payload.reseller_id = formData.reseller_id.trim();
      }
      if (formData.reseller_name.trim()) {
        payload.reseller_name = formData.reseller_name.trim();
      }
      if (formData.country_code.trim()) {
        payload.country_code = formData.country_code.trim().toUpperCase();
      }
      if (formData.currency.trim()) {
        payload.currency = formData.currency.trim().toUpperCase();
      }
      if (formData.phone_number.trim()) {
        payload.phone_number = formData.phone_number.trim();
      }
      if (Object.keys(accountOverrides).length) {
        payload.account_payload_overrides = accountOverrides;
      }
      if (Object.keys(locationOverrides).length) {
        payload.location_payload_overrides = locationOverrides;
      }

      const response = await api.post(
        apiEndpoints.provisionPosHubLocation(),
        payload,
      );

      if (response?.ok) {
        setResult(await response.json());
      } else {
        setError(await readProvisionError(response));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provisioning failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {selectedSubtab === 'poshub' && (
        <ComponentCard
          title="Provision POSHUB Location"
          className="overflow-hidden"
        >
          <form onSubmit={handleProvision} className="space-y-6">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
              This creates or reuses POSHUB account and location records. It
              leaves POS ordering pending and inactive until OAuth/POSHUB setup
              provides a connection ID.
            </div>

            <div>
              <Label>
                Store <span className="text-error-500">*</span>
              </Label>
              <SearchableSelect
                options={storeOptions}
                value={formData.store_id}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, store_id: value }))
                }
                placeholder="Select a store"
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Known Reseller UUID</Label>
                <Input
                  name="reseller_id"
                  value={formData.reseller_id}
                  onChange={handleChange}
                  placeholder="Optional reseller UUID"
                />
              </div>
              <div>
                <Label>Reseller Name</Label>
                <Input
                  name="reseller_name"
                  value={formData.reseller_name}
                  onChange={handleChange}
                  placeholder="AskArthur"
                />
              </div>
              <div>
                <Label>
                  Country Code <span className="text-error-500">*</span>
                </Label>
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      country_code: e.target.value,
                    }))
                  }
                  required
                  className={selectClassName}
                >
                  <option value="">Select country</option>
                  {countryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>
                  Currency <span className="text-error-500">*</span>
                </Label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  required
                  className={selectClassName}
                >
                  <option value="">Select currency</option>
                  {currencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Phone Number</Label>
                <Input
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="+14155552671"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Account Payload Overrides</Label>
                <textarea
                  name="account_payload_overrides"
                  value={formData.account_payload_overrides}
                  onChange={handleTextAreaChange}
                  placeholder="{}"
                  rows={5}
                  className={textAreaClassName}
                />
              </div>
              <div>
                <Label>Location Payload Overrides</Label>
                <textarea
                  name="location_payload_overrides"
                  value={formData.location_payload_overrides}
                  onChange={handleTextAreaChange}
                  placeholder="{}"
                  rows={5}
                  className={textAreaClassName}
                />
              </div>
            </div>

            {error && (
              <div className="whitespace-pre-wrap rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3.5 text-sm text-white shadow-theme-xs transition hover:bg-brand-600 md:w-auto ${
                submitting ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              {submitting ? 'Provisioning...' : 'Provision POSHUB Location'}
            </button>
          </form>

          {result && (
            <div className="mt-6 space-y-4 rounded-[24px] border border-[rgb(var(--app-brand)/0.22)] bg-[rgb(var(--app-brand)/0.08)] p-5">
              <div>
                <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
                  POSHUB provisioning completed
                </h3>
                <p className="mt-1 text-sm text-[rgb(var(--app-muted))]">
                  Status remains pending and inactive. OAuth/POSHUB connection
                  setup still needs to provide the connection ID before POS
                  ordering can be activated.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                <ResultRow
                  label="Account Created"
                  value={result.account_created ? 'Created' : 'Reused'}
                />
                <ResultRow
                  label="Location Created"
                  value={result.location_created ? 'Created' : 'Reused'}
                />
                <ResultRow
                  label="Connection Status"
                  value={result.connection?.status || 'pending'}
                />
                <ResultRow
                  label="Active"
                  value={result.connection?.is_active ? 'Yes' : 'No'}
                />
                <ResultRow
                  label="POSHUB Account ID"
                  value={
                    result.connection?.poshub_account_id ||
                    result.store_group_integration?.external_account_id
                  }
                />
                <ResultRow
                  label="POSHUB Location ID"
                  value={result.connection?.poshub_location_id}
                />
                <ResultRow
                  label="POSHUB Connection ID"
                  value={result.connection?.poshub_connection_id || 'Not set'}
                />
                <ResultRow
                  label="POSHUB Connection State"
                  value={
                    result.connection?.poshub_connection_status ||
                    'LOCATION_CREATED'
                  }
                />
                <ResultRow
                  label="Connection PK"
                  value={result.connection?.connection_pk}
                />
              </div>
            </div>
          )}
        </ComponentCard>
      )}
    </div>
  );
};

export default PosConfigTab;
